import { Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { LocalesService } from '@core/transloco/locales.service';
import { ApiService } from '@targx/services/api/api.service';
import qs from 'qs';
import { map, Observable, ReplaySubject, tap } from 'rxjs';
import { ProductSimple } from './product.model';

interface SolrResponse {
  facet_counts: Data;
  response: {
    docs: Data;
    numFound: number;
    numFoundExact: boolean;
    start: number;
  };
  responseHeader: Data;
}

@Injectable({
  providedIn: 'root',
})
export class MarketplaceService {
  private _products: Data;
  private _product: Data;

  public onProductsChanged: ReplaySubject<Data> = new ReplaySubject<Data>(1);
  public onProducfChanged: ReplaySubject<Data> = new ReplaySubject<Data>(1);

  public onMarketplaceHomeChanged: ReplaySubject<Data> =
    new ReplaySubject<Data>(1);

  public locale = { code: 'pt-PT' };
  constructor(
    private api: ApiService,
    private localeService: LocalesService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for footer
   */
  get products$(): Observable<Data> {
    return this.onProductsChanged.asObservable();
  }

  /**
   * Getter for footer
   */
  get product$(): Observable<Data> {
    return this.onProducfChanged.asObservable();
  }

  /**
   * Getter for footer
   */
  get mktHome$(): Observable<Data> {
    return this.onMarketplaceHomeChanged.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // filters[article_categories][slug][$contains]=historia
  // {"article_categories":{"slug":{"$contains":"historia"}}}
  // sort=createdAt:desc&pagination[page]=0&pagination[pageSize]=5
  // -----------------------------------------------------------------------------------------------------
  getPage(): Observable<Data> {
    // const qs = [
    //   'locale=pt-PT',
    //   'populate[0]=*',
    //   'populate[columnA][populate]=*',
    //   'populate[columnB][populate]=*',
    //   'populate[columnC][populate]=*',
    //   'populate[columnD][populate]=*',
    // ];
    this.locale = this.localeService.getActiveLocale();
    const data = {
      locale: this.locale.code,
      'populate[0]': '*',
      'populate[banner][populate]': '*',
      'populate[SEO][populate]': '*',
    };
    return this.api.get(`/marketplace`, data).pipe(
      tap((content) => {
        this.onMarketplaceHomeChanged.next(content);
      }),
    );
  }

  getAll(
    field = 'slug',
    query = '',
    page = 0,
    limit = 24,
    sort = 'createdAt:desc',
  ): Observable<Data> {
    this.locale = this.localeService.getActiveLocale();
    const data: Data = {
      locale: this.locale.code,
      populate: '*',
      'pagination[page]': page,
      'pagination[pageSize]': limit,
      sort: sort,
    };

    if (query) {
      data[`filters[product_category][${field}][$contains]`] = query;
    }

    return this.api.get(`/products`, data).pipe(
      tap((footer) => {
        this.onProductsChanged.next(footer);
      }),
    );
  }

  getProductsForWidget(
    query: Data = {},
    page = 0,
    limit = 24,
    sort = 'createdAt:desc',
  ): Observable<Data> {
    this.locale = this.localeService.getActiveLocale();
    const data: Data = {
      locale: this.locale.code,
      populate: '*',
      'pagination[page]': page,
      'pagination[pageSize]': limit,
      sort: sort,
      filters: {
        approved: true,
      },
    };

    if (query?.numberOfProducts) {
      data['pagination[pageSize]'] = query.numberOfProducts;
    }

    if (query?.productType) {
      data.filters.productType =
        query.productType === 'cars' ? 'car' : query.productType;
    }

    if (query.product_categories) {
      data.filters = {
        ...data.filters,
        $or: query.product_categories?.data?.map((category: Data) => {
          return {
            product_category: {
              slug: {
                $contains: category.attributes.slug,
              },
            },
          };
        }),
      };
    }

    const dataQuery = qs.stringify(data, {
      encodeValuesOnly: true,
    });

    return this.api.get(`/products`, '', '?' + dataQuery, false);
  }

  get(field = 'slug', query = ''): Observable<Data> {
    this.locale = this.localeService.getActiveLocale();
    const data: Data = {
      locale: this.locale.code,
      'populate[0]': '*',
      'populate[attributes][populate]': '*',
      'populate[city][populate]': '*',
      //      'populate[comments][populate]': '*',
      'populate[images][populate]': '*',
      'populate[product_brand][populate]': '*',
      'populate[product_category][populate]': '*',
      'populate[product_condition][populate]': '*',
      'populate[product_model][populate]': '*',
      //      'populate[saved_searches][populate]': '*',
      'populate[seller][populate]': '*',
      'populate[SEO][populate]': '*',
    };

    // if (query) {
    //   data[`filters[${field}][$contains]`] = query;
    // }

    return this.api.get(`/products/${query}`, data).pipe(
      tap((footer) => {
        this._product = footer;
        this.onProducfChanged.next(footer);
      }),
    );
  }

  getSolr(
    field = '*',
    query = '',
    page = 0,
    limit = 24,
    fq = [],
    sort = 'published_at_dt desc',
  ): Observable<Data> {
    const data: Data = {
      q: '*:*',
      start: page * limit,
      rows: limit,
      sort: sort,
    };

    if (query.length) {
      data.q = `*${query}*`;
    }

    if (fq.length) {
      data.fq = fq;
    }

    if (!data.fq) data.fq = [];

    data.fq.push('approved_b:true');
    data.fq.push('-product_type_s:automobile');

    /**
     * FAZ O PARSE DO OBJETO DATA PARA STRING
     */
    const dataQuery = qs.stringify(data, {
      encodeValuesOnly: true,
    });
    return this.api
      .getSolr<SolrResponse>(`/products`, '', '?' + dataQuery, false)
      .pipe(
        map((response: SolrResponse) => {
          const res: Data = {
            facets: response.facet_counts,
            products: response.response.docs,
            numFound: response.response.numFound,
            numFoundExact: response.response.numFoundExact,
            start: response.response.start,
          };

          res.products = res.products.map((product) => {
            const p = Object.assign(new ProductSimple(), product);
            p.bootstrap();
            return p;
          });
          return res;
        }),
        tap((products) => {
          this.onProductsChanged.next(products);
        }),
      );
  }

  getProductBySeller(sellerId: string): Observable<Data> {
    const data = {
      'populate[0]': '*',
      'populate[images][populate]': '*',
      'filters[seller][id]': sellerId,
      'filters[productType][$notIn]': 'automobile',
    };
    return this.api.get(`/products`, data);
  }
  getProductBySellerPaginacted(
    sellerId: string,
    page = 0,
    limit = 24,
    sort = 'createdAt:desc',
  ): Observable<Data> {
    const data = {
      'populate[0]': '*',
      'populate[images][populate]': '*',
      'filters[seller][id]': sellerId,
      'filters[productType][$notIn]': 'automobile',
      'filters[approved][$eq]': true,
      'pagination[page]': page,
      'pagination[pageSize]': limit,
    };
    return this.api.get(`/products`, data);
  }

  getAllProductBySellerOnlyCounts(sellerId: string): Observable<Data> {
    const data = {
      'fields[0]': 'name',
      'fields[1]': 'numberViews',
      'fields[2]': 'numberPhone',
      'fields[3]': 'numberClicks',
      'fields[4]': 'numberGoToSite',
      'filters[seller][id]': sellerId,
      'pagination[limit]': 1000,
    };
    return this.api.get(`/products`, data);
  }

  /**
   * Filtros de estado (aprovado/pendente/rejeitado/rascunho) partilhados entre
   * getSellerProductsPaged e countSellerProductsByStatus.
   */
  private _statusFilters(
    status: 'approved' | 'pending' | 'rejected' | 'draft',
  ): Record<string, any> {
    // "rejected" não tem default no schema — produtos antigos ficam com
    // rejected=null, não rejected=false. $ne:true não apanha null (semântica
    // SQL de 3 valores), por isso usamos $or (false OU null) em vez de $ne.
    const notRejected = {
      'filters[$or][0][rejected][$eq]': false,
      'filters[$or][1][rejected][$null]': true,
    };

    if (status === 'approved') {
      return {
        'filters[approved][$eq]': true,
        ...notRejected,
      };
    }
    if (status === 'rejected') {
      return { 'filters[rejected][$eq]': true };
    }
    if (status === 'pending') {
      // Só entra em pendentes o que vai para stock — vendido/restauro/reservado
      // já não precisa de aprovação. availability=null (registos antigos) conta
      // como in_stock (default do schema).
      return {
        'filters[approved][$eq]': false,
        'filters[submitted][$eq]': true,
        'filters[$and][0][$or][0][availability][$eq]': 'in_stock',
        'filters[$and][0][$or][1][availability][$null]': true,
        ...notRejected,
      };
    }
    return {
      'filters[submitted][$eq]': false,
      ...notRejected,
    };
  }

  /**
   * Conta anúncios do seller, sem trazer as linhas todas
   * (usa pagination[pageSize]=1 + meta.pagination.total).
   */
  countSellerProducts(
    sellerId: string,
    productType: 'car' | 'automobile' | 'all',
    extraFilters: Record<string, any> = {},
  ): Observable<number> {
    const data: Record<string, any> = {
      'filters[seller][id]': sellerId,
      'pagination[pageSize]': 1,
      ...extraFilters,
    };

    if (productType === 'automobile') {
      data['filters[productType][$eq]'] = 'automobile';
    } else if (productType === 'car') {
      data['filters[productType][$notIn]'] = 'automobile';
    }

    return this.api
      .get(`/products`, data)
      .pipe(map((res: any) => res?.meta?.pagination?.total ?? 0));
  }

  countSellerProductsByStatus(
    sellerId: string,
    productType: 'car' | 'automobile' | 'all',
    status: 'approved' | 'pending' | 'rejected' | 'draft',
  ): Observable<number> {
    return this.countSellerProducts(
      sellerId,
      productType,
      this._statusFilters(status),
    );
  }

  countSellerProductsInactive(
    sellerId: string,
    productType: 'car' | 'automobile' | 'all',
  ): Observable<number> {
    return this.countSellerProducts(sellerId, productType, {
      'filters[client_visivel_status_selected][$eq]': false,
    });
  }

  /**
   * Total de wishlists (produto guardado por um comprador) entre todos os
   * anúncios do seller.
   */
  countWishlists(sellerId: string): Observable<number> {
    const data = {
      'filters[product][seller][id][$eq]': sellerId,
      'pagination[pageSize]': 1,
    };

    return this.api
      .get(`/wishlists`, data)
      .pipe(map((res: any) => res?.meta?.pagination?.total ?? 0));
  }

  geAutomobiliaBySeller(sellerId: string): Observable<Data> {
    const data = {
      'populate[0]': '*',
      'populate[images][populate]': '*',
      'filters[seller][id]': sellerId,
      'filters[productType]': 'automobile',
    };
    return this.api.get(`/products`, data);
  }

  /**
   * Listagem privada (dashboard do seller) de classifieds/automobilia,
   * com paginação, pesquisa por nome e filtros por availability/status/visibilidade.
   */
  getSellerProductsPaged(
    sellerId: string,
    productType: 'car' | 'automobile',
    options: {
      page?: number;
      pageSize?: number;
      search?: string;
      availability?: string;
      status?: 'approved' | 'pending' | 'rejected' | 'draft';
      visible?: boolean;
    } = {},
  ): Observable<Data> {
    const {
      page = 1,
      pageSize = 12,
      search,
      availability,
      status,
      visible,
    } = options;

    const data: Record<string, any> = {
      'populate[0]': '*',
      'populate[images][populate]': '*',
      'filters[seller][id]': sellerId,
      'pagination[page]': page,
      'pagination[pageSize]': pageSize,
      'sort[0]': 'createdAt:desc',
    };

    if (productType === 'automobile') {
      data['filters[productType][$eq]'] = 'automobile';
    } else {
      data['filters[productType][$notIn]'] = 'automobile';
    }

    if (search) {
      data['filters[name][$containsi]'] = search;
    }
    if (availability) {
      data['filters[availability][$eq]'] = availability;
    }
    if (status) {
      Object.assign(data, this._statusFilters(status));
    }
    if (visible !== undefined && visible !== null) {
      data['filters[client_visivel_status_selected][$eq]'] = visible;
    }

    return this.api.get(`/products`, data);
  }

  delete(id: string): Observable<Data> {
    return this.api.delete(`/products/${id}`);
  }

  incremetView(): Observable<Data> {
    const id = this._product?.data?.id;
    const views = this._product?.data?.attributes?.numberViews;
    return this.api.put(`/products/${id}`, {
      data: { numberViews: views + 1 },
    });
  }

  /**
   * Incrementa o contador de cliques intencionais (numberClicks).
   * Deve ser chamado em: reveal do telefone, envio de mensagem, visita ao website.
   * NÃO deve ser chamado no page load.
   *
   * @param id  – ID do produto
   * @param prd – atributos planos do produto (this.product no detail component)
   */
  incrementClick(id: number, prd: any): Observable<Data> {
    // product can be flat (detail page) or nested (data.attributes)
    const clicks =
      prd?.numberClicks ?? prd?.data?.attributes?.numberClicks ?? 0;
    return this.api.put(`/products/${id}`, {
      data: { numberClicks: clicks + 1 },
    });
  }

  /**
   * Incrementa numberMessages quando um utilizador envia uma mensagem de contacto.
   */
  trackMessageContact(id: number, prd: any): Observable<Data> {
    const count =
      prd?.numberMessages ?? prd?.data?.attributes?.numberMessages ?? 0;
    return this.api.put(`/products/${id}`, {
      data: { numberMessages: count + 1 },
    });
  }

  showPhone(id, _product): Observable<Data> {
    const views = _product?.numberPhone ?? 0;
    return this.api.put(`/products/${id}`, {
      data: { numberPhone: views + 1 },
    });
  }
}
